import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Avatar, Box, Button, IconButton } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ShareLinkResultDialog from 'commons/ShareLinkResultDialog';
import ShareLinkSelectionDialog from 'commons/ShareLinkSelectionDialog';
import ShareLinkSettingsDialog from 'commons/ShareLinkSettingsDialog';
import SocialMedia from 'commons/SocialMedia';
import Text from 'commons/Text';
import API from 'configs/API';
import Colors from 'configs/Colors';
import { USER_ROLE_BY_ID } from 'configs/Constant';
import { ReactComponent as ShareIcon } from 'images/share.svg';
import { axiosGet, axiosPost } from 'utils/apis/axios';
import { ToastTopHelper } from 'utils/utils';

import ShareMyPageModal from '../ShareMyPageModal';
import useStyles from './styles';

const BoxInfo = ({ userProfile }) => {
  const { id } = useParams();
  const user = useSelector((state) => state.auth.account.user);
  const token = useSelector((state) => state.auth.account.token);
  const queryClient = useQueryClient();

  const isOwner = id === user?.uuid;

  const classes = useStyles();
  const { t } = useTranslation();
  const [isShareMyPageOpen, setIsShareMyPageOpen] = useState(false);

  const { visible_setting } = userProfile || {};

  // State for multi-step share link flow
  const [selectionAnchorEl, setSelectionAnchorEl] = useState(null);
  const [isOpenSettingsDialog, setIsOpenSettingsDialog] = useState(false);
  const [isOpenResultDialog, setIsOpenResultDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Data between steps
  const [selectedItems, setSelectedItems] = useState({ artworks: [], exhibitions: [] });
  const [shareLinkData, setShareLinkData] = useState({});
  const [createdLink, setCreatedLink] = useState('');

  // Real data from API
  const [exhibitions, setExhibitions] = useState([]);
  const [loadingExhibitions, setLoadingExhibitions] = useState(false);

  // Use useQuery for artworks
  const userUuid = isOwner ? user?.uuid : id;
  const { data: artworksData, isLoading: loadingArtworks } = useQuery({
    queryKey: [API.ARTWORK.LIST_ARTWORK_SELECTION, { user_uuid: userUuid, token }],
    enabled: isOwner && !!userUuid && !!token,
  });

  const artworks = artworksData?.results || artworksData || [];

  const fetchExhibitions = useCallback(async () => {
    setLoadingExhibitions(true);
    try {
      const { success, data } = await axiosGet(API.AUTH.GROUP_EXHIBITION.LIST);
      if (success && data) {
        setExhibitions(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching exhibitions:', error);
    } finally {
      setLoadingExhibitions(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner && token) {
      fetchExhibitions();
    }
  }, [isOwner, token, fetchExhibitions]);

  const handleStartShare = (e) => {
    setSelectionAnchorEl(e?.currentTarget || null);
  };

  const handleSelectionNext = (items) => {
    setSelectedItems(items);
    setSelectionAnchorEl(null);
    setIsOpenSettingsDialog(true);
  };

  const handleOpenShareMyPage = () => {
    setSelectionAnchorEl(null);
    setIsShareMyPageOpen(true);
  };

  const handleSettingsNext = async (settingsData) => {
    setShareLinkData(settingsData);
    setIsLoading(true);

    try {
      const { ...createPayload } = settingsData;
      const { success, data } = await axiosPost(API.ARTWORK.CREATE_SHARE_LINK, createPayload);
      if (success) {
        const listUrl = API.ARTWORK.SHARE_LINK_RECIPIENTS_TYPE_ARTWORK(user?.id);
        queryClient.invalidateQueries({ queryKey: [listUrl, { shareType: 'artwork' }] });

        const uuid = data.share_url.split('/').filter(Boolean).pop();
        const feShareUrl = `${window.location.origin}/share/${uuid}`;
        setCreatedLink(feShareUrl);
        setIsOpenSettingsDialog(false);
        setIsOpenResultDialog(true);
      } else {
        ToastTopHelper.error(t('create_share_link_failed'));
      }
    } catch (error) {
      ToastTopHelper.error(t('create_share_link_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSelection = () => {
    setSelectionAnchorEl(null);
  };

  const handleCloseSettings = () => {
    setIsOpenSettingsDialog(false);
  };

  const handleCloseResult = () => {
    setIsOpenResultDialog(false);
  };

  const handleCloseShareMyPage = () => {
    setIsShareMyPageOpen(false);
  };

  return (
    <Box className={classes.wrapBoxInfo}>
      <Avatar
        src={userProfile?.avatar || ''}
        className={classes.avatar}
      />

      <div className={classes.wrapNameUserProfile}>
        <Text
          type="lg"
          mt={16}
          fontWeightBold
        >
          {userProfile?.name || ''}
        </Text>
      </div>

      <Box className={classes.wrapRoleId}>
        <Text
          className={classes.roleText}
          type="sm"
        >
          {t(`${USER_ROLE_BY_ID[userProfile?.role] || ''}`)}
        </Text>

        {userProfile?.uuid_user && (
          <>
            <Text
              className={classes.bulletSeparator}
              type="sm"
            >
              •
            </Text>
            <Box className={classes.idWrapper}>
              <Text
                className={classes.idText}
                type="sm"
              >
                {`id-${userProfile.uuid_user.slice(0, 6)}...${userProfile.uuid_user.slice(-3)}`}
              </Text>
              <IconButton
                className={classes.copyButton}
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(userProfile.uuid_user);
                  ToastTopHelper.success(t('copied'));
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16, color: Colors.Grey10 }} />
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      {userProfile?.live_at && (
        <Box className={classes.wrapLocation}>
          <Text
            fontWeightRegular
            color={Colors.Grey8}
            type="xs"
          >
            {userProfile?.live_at}
          </Text>
        </Box>
      )}

      {visible_setting?.is_public_social_media && <SocialMedia socials={userProfile?.socials} />}

      {/* Share button */}
      {user?.uuid && isOwner && (
        <Button
          onClick={handleStartShare}
          variant="outlined"
          fullWidth
          className={classes.buttonShareLink}
          startIcon={<ShareIcon />}
          sx={{ mt: 1 }}
        >
          {t('share_link_show')}
        </Button>
      )}

      {/* Share Link Selection Dialog */}
      <ShareLinkSelectionDialog
        open={Boolean(selectionAnchorEl)}
        anchorEl={selectionAnchorEl}
        onClose={handleCloseSelection}
        onNext={handleSelectionNext}
        onShareMyPage={handleOpenShareMyPage}
        userProfile={userProfile}
        artworks={artworks}
        exhibitions={exhibitions}
        loading={loadingArtworks || loadingExhibitions}
      />

      {/* Share Link Settings Dialog */}
      <ShareLinkSettingsDialog
        open={isOpenSettingsDialog}
        onClose={handleCloseSettings}
        onNext={handleSettingsNext}
        selectedItems={selectedItems}
        artworks={artworks}
        exhibitions={exhibitions}
        loading={isLoading}
      />

      {/* Share Link Result Dialog */}
      <ShareLinkResultDialog
        open={isOpenResultDialog}
        onClose={handleCloseResult}
        shareLinkData={shareLinkData}
        createdLink={createdLink}
      />

      <ShareMyPageModal
        open={isShareMyPageOpen}
        onClose={handleCloseShareMyPage}
      />
    </Box>
  );
};

export default BoxInfo;
